/**
 * QualitativeAuditList.jsx — Qualitative tab content for AuditList.
 *
 * Same <table>/<thead>/<tbody> structure the Quantitative tab uses, just
 * with a slimmer column set: select / # / KPI / Status. Qualitative count
 * is always 1, so we don't render Value / Pending / Approved / Rejected /
 * Actions columns. Click anywhere on the row to expand the accordion below
 * (the body is a colSpan'd <tr> immediately after each row).
 *
 * Bulk select + select-all reuse the same `audit-checkbox` + `al2-kpi-row`
 * classes from the quantitative tab so hover highlight and checkbox styling
 * match exactly.
 */
import React, { useState, useEffect, Fragment } from "react";
import { HighlightText, StatusBadge } from "./AuditIcons";
import { fetchQualitativeQuestions } from "./auditApi";
import QualitativeAuditAccordion from "./QualitativeAuditAccordion";

const QualitativeRow = ({
  item,
  index,
  expanded,
  selected,
  actionable,
  onToggleExpand,
  onToggleSelect,
  searchQuery,
  fyId,
  auditType,
  onActionComplete,
  question,
}) => {
  const status = (item.auditStatus || "PENDING").toUpperCase();
  return (
    <Fragment>
      <tr
        className="al2-kpi-row"
        onClick={() => onToggleExpand(item.questionId)}
        style={{
          background: selected ? "#eff6ff" : expanded ? "#f8faff" : "#fff",
          cursor: "pointer",
        }}
      >
        <td style={{ width: 44, padding: "12px 8px 12px 16px", textAlign: "center", verticalAlign: "middle" }}>
          <input
            type="checkbox"
            className="audit-checkbox"
            checked={selected}
            disabled={!actionable}
            onClick={e => e.stopPropagation()}
            onChange={() => onToggleSelect(item.questionId)}
            style={{ cursor: actionable ? "pointer" : "not-allowed", opacity: actionable ? 1 : 0.35 }}
          />
        </td>
        <td style={{ width: 36, padding: "12px 4px", textAlign: "center", verticalAlign: "middle", fontSize: 11, color: "#d1d5db", fontWeight: 600 }}>
          {index + 1}
        </td>
        <td style={{ padding: "12px 12px 12px 4px", verticalAlign: "middle" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", lineHeight: 1.45, letterSpacing: "-.01em" }}>
            <HighlightText text={item.title || `KPI #${item.questionId}`} query={searchQuery} />
          </div>
        </td>
        <td style={{ width: 140, padding: "12px 20px 12px 8px", textAlign: "right", verticalAlign: "middle" }}>
          <StatusBadge status={status} level={item.pendingLevel} />
        </td>
      </tr>
      {expanded && (
        <tr style={{ background: "#f8faff" }}>
          <td colSpan={4} style={{ padding: 0 }}>
            <QualitativeAuditAccordion
              item={item}
              fyId={fyId}
              auditType={auditType}
              onActionComplete={onActionComplete}
              question={question}
            />
          </td>
        </tr>
      )}
    </Fragment>
  );
};

const QualitativeAuditList = ({
  items,
  searchQuery,
  fyId,
  auditType,
  onActionComplete,
  selectedIds,
  onToggleSelect,
  actionableSet,
  allSelected,
  someSelected,
  onToggleAll,
}) => {
  const [expandedId, setExpandedId] = useState(null);
  const toggleExpand = (id) => setExpandedId(prev => (prev === id ? null : id));

  // Fetch full qualitative question shapes (with `details`) once per FY change.
  // TabularDynamicForm needs `details` to decide its layout (and to dispatch
  // into FullyDynamicForm). Mirrors Qualitative.jsx's pattern.
  const [questionsById, setQuestionsById] = useState({});
  useEffect(() => {
    let cancelled = false;
    if (!fyId) { setQuestionsById({}); return; }
    fetchQualitativeQuestions({ financialYearId: fyId })
      .then(rows => {
        if (cancelled) return;
        const map = {};
        for (const q of rows) {
          const id = q.questionId ?? q.id;
          if (id != null) map[id] = q;
        }
        setQuestionsById(map);
      })
      .catch(err => console.error("fetchQualitativeQuestions:", err));
    return () => { cancelled = true; };
  }, [fyId]);

  if (!items.length) {
    return (
      <div style={{ background: "#fff", borderRadius: 9, border: "1px solid #e5e7eb", padding: "64px 24px", textAlign: "center" }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
          No qualitative KPIs in audit queue
        </div>
        <div style={{ fontSize: 11.5, color: "#9ca3af" }}>
          When tabular answers are sent for audit, they will appear here.
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: "#fff", borderRadius: 9, border: "1px solid #e5e7eb",
      overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,.04), 0 4px 14px rgba(0,0,0,.04)",
    }}>
      <div style={{ overflowX: "auto", borderRadius: 9 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
        <thead>
          <tr style={{ background: "#f9fafb", borderBottom: "1.5px solid #e5e7eb" }}>
            <th style={{ width: 44, padding: "10px 8px 10px 16px", textAlign: "center" }}>
              <input
                type="checkbox"
                className="audit-checkbox"
                checked={!!allSelected}
                ref={el => { if (el) el.indeterminate = !!someSelected && !allSelected; }}
                disabled={!actionableSet || actionableSet.size === 0}
                onChange={onToggleAll}
              />
            </th>
            <th style={{ width: 36, padding: "10px 4px", fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".07em", textAlign: "center" }}>#</th>
            <th style={{ padding: "10px 12px 10px 4px", fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".07em", textAlign: "left" }}>KPI</th>
            <th style={{ width: 140, padding: "10px 20px 10px 8px", fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".07em", textAlign: "right" }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <QualitativeRow
              key={item.questionId}
              item={item}
              index={idx}
              expanded={expandedId === item.questionId}
              selected={selectedIds?.has(item.questionId) || false}
              actionable={!!actionableSet?.has(item.questionId)}
              onToggleExpand={toggleExpand}
              onToggleSelect={onToggleSelect}
              searchQuery={searchQuery}
              fyId={fyId}
              auditType={auditType}
              onActionComplete={onActionComplete}
              question={questionsById[item.questionId] || null}
            />
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default QualitativeAuditList;
